/**
 * @fileoverview Chat Context - Background Service for managing chat sessions
 *
 * This context acts as a global registry for active chat sessions, allowing:
 * - Persistence of chat state across component unmounts
 * - Continuation of streaming responses when navigating away
 * - Multiple concurrent chat sessions
 * - Centralized AbortController management
 */

import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import type { UIBlock, RenderPayload } from '@/types/ui-blocks';
import type { CardBlock } from '@/hooks/useCards';
import type { StreamingParser } from '@/lib/streaming-parser';

type ToolCall = {
  id: string;
  name: string;
  description?: string;
  requires_approval?: boolean;
  hide_from_tool_call_chat_ui?: boolean
};

export type ChatEntry =
  | { id: string; kind: "assistant"; content: string; chat_history_id?: string; behaviour_action_state?: Record<string, unknown> }
  | { id: string; kind: "assistant-structured"; payload: RenderPayload; chat_history_id?: string; behaviour_action_state?: Record<string, unknown> }
  | { id: string; kind: "assistant-progressive"; blocks: UIBlock[]; isComplete?: boolean; chat_history_id?: string; behaviour_action_state?: Record<string, unknown> }
  | { id: string; kind: "user"; content: string; blocks?: UIBlock[]; cardBlock?: CardBlock; chat_history_id?: string }
  | { id: string; kind: "assistant-typing" }
  | { id: string; kind: "reasoning"; steps: string[]; isStreaming: boolean; startTime?: number; duration?: number }
  | { id: string; kind: "tool-approval"; toolCalls: ToolCall[]; requireApproval?: boolean; resolved?: boolean; resolution?: 'approved' | 'rejected' };

export type Suggestion = {
  id: string | number;
  text: string;
  icon?: React.ReactNode;
  prefillOnly?: boolean;
};

interface ChatSession {
  sessionId: string;
  messages: ChatEntry[];
  isLoading: boolean;
  streamingContent: string;
  awaitingToolAck: boolean;
  pendingToolCalls: ToolCall[];
  shouldStartThinkingSteps: boolean;
  typingMessageId: string | null;
  progressiveMessageId: string | null;
  reasoningMessageId: string | null;
  lastClosedReasoningId: string | null;
  dynamicSuggestions: Suggestion[];
  controller: AbortController | null;
  thinkingStepsTimer: NodeJS.Timeout | null;
  thinkingStepsInterval: NodeJS.Timeout | null;
  thinkingSteps: string[];

  // LangDiff streaming state
  streamingParser: StreamingParser | null;
  streamingTextContent: Map<number, string>; // blockIndex -> accumulated content
  streamingBlockIndices: Set<number>; // blocks currently streaming
  pendingBlockType: string | null; // type of block being loaded (for loading indicators)

  // Deferred tool acknowledgment (sent after stream completes)
  deferredToolAckNeeded: boolean;
}

interface ChatContextValue {
  sessions: Record<string, ChatSession>;
  registerSession: (sessionId: string, initialMessages?: ChatEntry[]) => void;
  getSession: (sessionId: string) => ChatSession | undefined;
  updateSession: (sessionId: string, updates: Partial<ChatSession>) => void;
  setMessages: (sessionId: string, messages: ChatEntry[] | ((prev: ChatEntry[]) => ChatEntry[])) => void;
  setLoading: (sessionId: string, isLoading: boolean) => void;
  setStreamingContent: (sessionId: string, content: string | ((prev: string) => string)) => void;
  setAwaitingToolAck: (sessionId: string, awaiting: boolean) => void;
  setPendingToolCalls: (sessionId: string, calls: ToolCall[]) => void;
  setShouldStartThinkingSteps: (sessionId: string, should: boolean) => void;
  setTypingMessageId: (sessionId: string, id: string | null) => void;
  setProgressiveMessageId: (sessionId: string, id: string | null) => void;
  setReasoningMessageId: (sessionId: string, id: string | null) => void;
  setLastClosedReasoningId: (sessionId: string, id: string | null) => void;
  setDynamicSuggestions: (sessionId: string, suggestions: Suggestion[]) => void;
  getController: (sessionId: string) => AbortController | null;
  setController: (sessionId: string, controller: AbortController | null) => void;
  clearSession: (sessionId: string) => void;
  abortSession: (sessionId: string) => void;

  // LangDiff streaming methods
  setStreamingParser: (sessionId: string, parser: StreamingParser | null) => void;
  appendStreamingTextContent: (sessionId: string, blockIndex: number, chunk: string) => void;
  setStreamingBlockIndices: (sessionId: string, indices: Set<number>) => void;
  markBlockStreaming: (sessionId: string, blockIndex: number, isStreaming: boolean) => void;
  clearStreamingState: (sessionId: string) => void;
  setPendingBlockType: (sessionId: string, blockType: string | null) => void;
  setDeferredToolAckNeeded: (sessionId: string, needed: boolean) => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

const createEmptySession = (sessionId: string): ChatSession => ({
  sessionId,
  messages: [],
  isLoading: false,
  streamingContent: "",
  awaitingToolAck: false,
  pendingToolCalls: [],
  shouldStartThinkingSteps: false,
  typingMessageId: null,
  progressiveMessageId: null,
  reasoningMessageId: null,
  lastClosedReasoningId: null,
  dynamicSuggestions: [],
  controller: null,
  thinkingStepsTimer: null,
  thinkingStepsInterval: null,
  thinkingSteps: [],

  // LangDiff streaming state
  streamingParser: null,
  streamingTextContent: new Map(),
  streamingBlockIndices: new Set(),
  pendingBlockType: null,

  // Deferred tool acknowledgment
  deferredToolAckNeeded: false,
});

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sessions, setSessions] = useState<Record<string, ChatSession>>({});
  const sessionsRef = useRef<Record<string, ChatSession>>({});

  // Keep ref in sync with state
  sessionsRef.current = sessions;

  const registerSession = useCallback((sessionId: string, initialMessages: ChatEntry[] = []) => {
    setSessions((prev) => {
      if (prev[sessionId]) {
        // Session already exists, don't overwrite
        return prev;
      }
      return {
        ...prev,
        [sessionId]: {
          ...createEmptySession(sessionId),
          messages: initialMessages,
        },
      };
    });
  }, []);

  const getSession = useCallback((sessionId: string): ChatSession | undefined => {
    return sessionsRef.current[sessionId];
  }, []);

  const updateSession = useCallback((sessionId: string, updates: Partial<ChatSession>) => {
    setSessions((prev) => {
      let session = prev[sessionId];

      // Auto-register session if it doesn't exist
      if (!session) {
        session = createEmptySession(sessionId);
      }

      return {
        ...prev,
        [sessionId]: { ...session, ...updates },
      };
    });
  }, []);

  const setMessages = useCallback(
    (sessionId: string, messages: ChatEntry[] | ((prev: ChatEntry[]) => ChatEntry[])) => {
      setSessions((prev) => {
        let session = prev[sessionId];

        // Auto-register session if it doesn't exist
        if (!session) {
          session = createEmptySession(sessionId);
        }

        const newMessages = typeof messages === 'function' ? messages(session.messages) : messages;
        return {
          ...prev,
          [sessionId]: { ...session, messages: newMessages },
        };
      });
    },
    []
  );

  const setLoading = useCallback((sessionId: string, isLoading: boolean) => {
    updateSession(sessionId, { isLoading });
  }, [updateSession]);

  const setStreamingContent = useCallback(
    (sessionId: string, content: string | ((prev: string) => string)) => {
      setSessions((prev) => {
        const session = prev[sessionId];
        if (!session) return prev;
        const newContent = typeof content === 'function' ? content(session.streamingContent) : content;
        return {
          ...prev,
          [sessionId]: { ...session, streamingContent: newContent },
        };
      });
    },
    []
  );

  const setAwaitingToolAck = useCallback((sessionId: string, awaiting: boolean) => {
    updateSession(sessionId, { awaitingToolAck: awaiting });
  }, [updateSession]);

  const setPendingToolCalls = useCallback((sessionId: string, calls: ToolCall[]) => {
    updateSession(sessionId, { pendingToolCalls: calls });
  }, [updateSession]);

  const setShouldStartThinkingSteps = useCallback((sessionId: string, should: boolean) => {
    updateSession(sessionId, { shouldStartThinkingSteps: should });
  }, [updateSession]);

  const setTypingMessageId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { typingMessageId: id });
  }, [updateSession]);

  const setProgressiveMessageId = useCallback((sessionId: string, id: string | null) => {
    // Immediately update ref for synchronous access in streaming callbacks
    if (sessionsRef.current[sessionId]) {
      sessionsRef.current[sessionId] = {
        ...sessionsRef.current[sessionId],
        progressiveMessageId: id,
      };
    }
    updateSession(sessionId, { progressiveMessageId: id });
  }, [updateSession]);

  const setReasoningMessageId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { reasoningMessageId: id });
  }, [updateSession]);

  const setLastClosedReasoningId = useCallback((sessionId: string, id: string | null) => {
    updateSession(sessionId, { lastClosedReasoningId: id });
  }, [updateSession]);

  const setDynamicSuggestions = useCallback((sessionId: string, suggestions: Suggestion[]) => {
    updateSession(sessionId, { dynamicSuggestions: suggestions });
  }, [updateSession]);

  const getController = useCallback((sessionId: string): AbortController | null => {
    return sessionsRef.current[sessionId]?.controller || null;
  }, []);

  const setController = useCallback((sessionId: string, controller: AbortController | null) => {
    updateSession(sessionId, { controller });
  }, [updateSession]);

  const clearSession = useCallback((sessionId: string) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      // Clean up timers
      if (session.thinkingStepsTimer) clearTimeout(session.thinkingStepsTimer);
      if (session.thinkingStepsInterval) clearInterval(session.thinkingStepsInterval);

      // Create fresh session
      return {
        ...prev,
        [sessionId]: createEmptySession(sessionId),
      };
    });
  }, []);

  const abortSession = useCallback((sessionId: string) => {
    const session = sessionsRef.current[sessionId];
    if (session?.controller) {
      session.controller.abort();
      updateSession(sessionId, { controller: null, isLoading: false });
    }
  }, [updateSession]);

  // LangDiff streaming methods
  const setStreamingParser = useCallback((sessionId: string, parser: StreamingParser | null) => {
    // CRITICAL: Immediately update ref for synchronous access
    // This prevents race conditions where multiple CONTENT_STREAM events
    // arrive before React re-renders, each creating a new parser
    if (sessionsRef.current[sessionId]) {
      sessionsRef.current[sessionId] = {
        ...sessionsRef.current[sessionId],
        streamingParser: parser,
      };
    }
    // Also update state for React re-renders
    updateSession(sessionId, { streamingParser: parser });
  }, [updateSession]);

  const appendStreamingTextContent = useCallback((sessionId: string, blockIndex: number, chunk: string) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      const newMap = new Map(session.streamingTextContent);
      const currentContent = newMap.get(blockIndex) || '';
      newMap.set(blockIndex, currentContent + chunk);

      return {
        ...prev,
        [sessionId]: { ...session, streamingTextContent: newMap },
      };
    });
  }, []);

  const setStreamingBlockIndices = useCallback((sessionId: string, indices: Set<number>) => {
    updateSession(sessionId, { streamingBlockIndices: indices });
  }, [updateSession]);

  const markBlockStreaming = useCallback((sessionId: string, blockIndex: number, isStreaming: boolean) => {
    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      const newIndices = new Set(session.streamingBlockIndices);
      if (isStreaming) {
        newIndices.add(blockIndex);
      } else {
        newIndices.delete(blockIndex);
      }

      return {
        ...prev,
        [sessionId]: { ...session, streamingBlockIndices: newIndices },
      };
    });
  }, []);

  const clearStreamingState = useCallback((sessionId: string) => {
    // Reset parser outside setState to avoid side effects in callback
    const parser = sessionsRef.current[sessionId]?.streamingParser;
    parser?.reset();

    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) return prev;

      return {
        ...prev,
        [sessionId]: {
          ...session,
          streamingParser: null,
          streamingTextContent: new Map(),
          streamingBlockIndices: new Set(),
          pendingBlockType: null,
        },
      };
    });
  }, []);

  const setPendingBlockType = useCallback((sessionId: string, blockType: string | null) => {
    updateSession(sessionId, { pendingBlockType: blockType });
  }, [updateSession]);

  const setDeferredToolAckNeeded = useCallback((sessionId: string, needed: boolean) => {
    // Immediately update ref for synchronous access in streaming callbacks
    if (sessionsRef.current[sessionId]) {
      sessionsRef.current[sessionId] = {
        ...sessionsRef.current[sessionId],
        deferredToolAckNeeded: needed,
      };
    }
    updateSession(sessionId, { deferredToolAckNeeded: needed });
  }, [updateSession]);

  const value: ChatContextValue = {
    sessions,
    registerSession,
    getSession,
    updateSession,
    setMessages,
    setLoading,
    setStreamingContent,
    setAwaitingToolAck,
    setPendingToolCalls,
    setShouldStartThinkingSteps,
    setTypingMessageId,
    setProgressiveMessageId,
    setReasoningMessageId,
    setLastClosedReasoningId,
    setDynamicSuggestions,
    getController,
    setController,
    clearSession,
    abortSession,

    // LangDiff streaming methods
    setStreamingParser,
    appendStreamingTextContent,
    setStreamingBlockIndices,
    markBlockStreaming,
    clearStreamingState,
    setPendingBlockType,
    setDeferredToolAckNeeded,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within ChatProvider');
  }
  return context;
};
